import { ArrowRight } from "lucide-react";
import { useMetaTags } from "../components/MetaTags";
import { RecallChallenge } from "../challenge/RecallChallenge";

const BASE = "https://omnicanvasnotes.com";
const URL = `${BASE}/challenge`;

// The page exists to be shared, so it gets its own card rather than the site
// default. Source and re-render steps: scripts/og-cards/challenge.html.
const OG_IMAGE =
  "https://pub-530c8de906034aa3846ea946c751a5aa.r2.dev/og-challenge.png";

const APP_HREF =
  "https://app.omnicanvasnotes.com/?mode=try&action=new-note" +
  "&utm_source=marketing&utm_medium=challenge&utm_campaign=recall_test" +
  "&utm_content=bottom_cta";

function jsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Game",
        name: "The Recall Test",
        description:
          "A 60-second browser game about the tradeoff every notetaker makes: capture the meeting fast, or capture it so you can find it again.",
        url: URL,
        image: OG_IMAGE,
        genre: "Educational",
        playMode: "SinglePlayer",
        gamePlatform: "Web browser",
        publisher: { "@type": "Organization", name: "OmniCanvas" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: BASE },
          { "@type": "ListItem", position: 2, name: "The Recall Test", item: URL },
        ],
      },
    ],
  });
}

export function Challenge() {
  useMetaTags({
    title: "The Recall Test — Can You Take Notes You Can Actually Find?",
    description:
      "A 60-second game about the tradeoff every notetaker makes. Capture a meeting under a clock, then try to answer five questions from the notes you kept. Free, no signup.",
    url: URL,
    ogImage: OG_IMAGE,
  });

  return (
    <main className="bg-white pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLd() }}
      />

      <header className="mx-auto max-w-3xl px-6 pt-12 pb-8">
        <span className="mb-4 inline-block rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-600">
          Free · Plays in your browser
        </span>
        <h1 className="mb-4 text-3xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
          The Recall Test
        </h1>
        <p className="text-lg text-slate-600 md:text-xl">
          Every note you take is a bet: capture it fast, or capture it so you
          can find it again. You rarely get both. Here is sixty seconds to prove
          otherwise.
        </p>
      </header>

      <section className="mx-auto max-w-3xl px-6">
        <RecallChallenge />
      </section>

      <section className="mx-auto max-w-3xl px-6 pt-14">
        <h2 className="mb-4 text-2xl font-bold text-slate-900">
          Why the game is unfair, and why that's the point
        </h2>
        <div className="space-y-4 text-slate-600">
          <p>
            Nothing in the recall round is rigged against the pile. Every line
            you dumped is still there, spelled exactly the way you captured it,
            in the order you took it down. The only thing the placed lines have
            is a header above them.
          </p>
          <p>
            That turns out to be the whole game. Searching a pile is a linear
            scan — you read until you hit the line you want, and the clock runs
            the entire time. Searching four short clusters is a jump: you know
            which one a budget number lives in before you start reading. Under
            time pressure that difference is the difference between answering
            and guessing.
          </p>
          <p>
            The catch is that placing costs something at capture time, and in
            this game it costs you lines. That is honest — in a linear notes
            app, structure is work you do <em>instead of</em> listening. Folders,
            tags and headings all get paid for out of the same budget: your
            attention, while the room is still talking.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 pt-10">
        <h2 className="mb-4 text-2xl font-bold text-slate-900">
          What changes the tradeoff
        </h2>
        <div className="space-y-4 text-slate-600">
          <p>
            Structure gets cheap when it stops being a decision. On an infinite
            canvas you do not choose a folder — you drop the line near the
            things it belongs with, which is a movement rather than a
            classification. The budget number lands next to the budget
            conversation because that is where your hand already was.
          </p>
          <p>
            That is what OmniCanvas is for: spatial notes where placing is a
            gesture, not a filing step, so the note you take in a hurry is still
            the note you can find in November. It is the same argument this game
            makes, minus the clock.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-14">
        <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 px-8 py-12 text-center shadow-xl shadow-indigo-100 md:px-12">
          <h2 className="mb-3 text-2xl font-extrabold text-white md:text-3xl">
            Now take the notes without the clock
          </h2>
          <p className="mx-auto mb-7 max-w-xl text-indigo-100">
            The live demo runs the real canvas engine — no account, nothing
            saved. Or start a canvas that keeps what you put on it.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="/demo?utm_source=marketing&utm_medium=challenge&utm_campaign=recall_test"
              className="group inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-3.5 font-bold text-indigo-700 shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl"
            >
              Try the live canvas
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href={APP_HREF}
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-3.5 font-bold text-white transition-colors hover:bg-white/10"
            >
              Start a canvas free
            </a>
          </div>
          <p className="mt-4 text-xs text-indigo-200">
            No credit card · 30 days free
          </p>
        </div>
      </section>
    </main>
  );
}
